import { COMPARISON_OPS, INDICATOR_KINDS, INDICATOR_OUTPUTS, TIMEFRAME_MS, catalogEntry, formulaLeaves, indicatorAutoLabel, indicatorExtensionProblems, isConditionGroup, isMultiOutput, offsetProblem, operandOffset, POSITION_SIZE_MODES, PRICE_FIELDS, PROFILE_IDS, TRADE_DIRECTIONS, } from "@strat/format";
import { timeframeFit } from "./indicators/timeframe.js";
import { specStartupBars } from "./indicators/warmup.js";
/**
 * Most bars a live bot keeps in its window. A daily indicator under a
 * one-minute bot is 1,440 bars per day of warm-up, recomputed on every bar;
 * past this the window is the cost, and the strategy belongs on a slower run.
 */
export const MAX_LIVE_WINDOW_BARS = 50_000;
export const COMPAT_LEVELS = ["supported", "lossy", "ignored", "rejected"];
/**
 * Highest leverage a live paper bot will run.
 *
 * Leverage itself is the shared `stepBar`'s, so a bot liquidates exactly where
 * its backtest does (the parity tests run leveraged fixtures to prove it). The
 * cap is a product line, not an engine limit: past 10× a single closed bar's
 * range decides most trades, and a paper record that is mostly liquidations
 * teaches nothing.
 */
export const MAX_LIVE_LEVERAGE = 10;
/**
 * Why this strategy cannot run live on this kind of market, or null.
 *
 * The profile is market-agnostic — a strategy names no market — so this is the
 * one rule that needs both: leverage and perpetual funding exist on futures
 * only. A spot bot at 3×, or one "paying funding" on a coin with no funding,
 * would model an account the hub does not offer. Shared by every deploy path.
 */
export function liveMarketProblem(spec, marketType) {
    if (marketType === "futures")
        return null;
    const leverage = Math.max(1, spec.risk?.leverage ?? 1);
    if (leverage > 1)
        return `This strategy uses ${leverage}× leverage, which needs a futures market. Pick the futures market, or set leverage to 1×.`;
    if (spec.risk?.applyFunding)
        return "This strategy charges perpetual funding, which only futures markets have. Pick the futures market, or turn funding off.";
    return null;
}
/**
 * Why this strategy cannot run on bars of this length, or null: an indicator
 * on a slower chart that these bars cannot build — faster than the run, or not
 * a whole multiple of it. Its series would be null on every bar, which looks
 * like a strategy that never fires rather than a run that could not work.
 *
 * Unknown bar length is not a problem here; the caller that knows it asks.
 */
export function timeframeProblemForBar(spec, barMs, runTimeframe) {
    if (!barMs)
        return null;
    for (const def of spec.indicators ?? []) {
        if (def?.timeframe === undefined)
            continue;
        const fit = timeframeFit(def.timeframe, barMs);
        if (fit !== "incompatible")
            continue;
        const name = def.label?.trim() || indicatorAutoLabel(def);
        const run = runTimeframe ?? `${Math.round(barMs / 60_000)}m`;
        return `${name} is computed on the ${def.timeframe} chart, which cannot be built from ${run} bars. Run it on a timeframe that divides ${def.timeframe} (for example ${def.timeframe === "5m" ? "1m" : "5m"}), or change that indicator's timeframe.`;
    }
    return null;
}
/** `timeframeProblemForBar`, for a run named by its timeframe ("5m", "1h"). */
export function timeframeProblem(spec, timeframe) {
    return timeframeProblemForBar(spec, TIMEFRAME_MS[timeframe], timeframe);
}
/**
 * `timeframeProblem`, plus the one thing only a bot has: a window to keep.
 * Shared by every deploy path and by the live engine itself.
 */
export function liveTimeframeProblem(spec, timeframe) {
    const problem = timeframeProblem(spec, timeframe);
    if (problem)
        return problem;
    const barMs = TIMEFRAME_MS[timeframe];
    return liveWindowProblem(spec, barMs, timeframe);
}
export function liveWindowProblem(spec, barMs, timeframe) {
    const bars = specStartupBars(spec, barMs);
    if (bars <= MAX_LIVE_WINDOW_BARS)
        return null;
    return `A bot would have to keep ${bars.toLocaleString("en-US")} bars of history${timeframe ? ` of ${timeframe}` : ""} to warm this strategy up; the most is ${MAX_LIVE_WINDOW_BARS.toLocaleString("en-US")}. Run it on a slower timeframe, or shorten the slow indicator.`;
}
/** Whether a rule, or a formula, reads a value from an earlier bar. */
function looksBack(s) {
    let found = false;
    const walk = (node) => {
        if (!node || found)
            return;
        if (isConditionGroup(node))
            return node.children.forEach(walk);
        if ([node.left, node.right].some((side) => side && operandOffset(side) > 0))
            found = true;
    };
    walk(s.entry);
    walk(s.exit);
    return (found ||
        (s.indicators ?? []).some((d) => d?.formula !== undefined && formulaLeaves(d.formula).some((l) => operandOffset(l) > 0)));
}
const both = (level) => ({
    research: level,
    deployable: level,
});
export const CAPABILITIES = [
    {
        id: "leverage",
        field: "risk.leverage",
        title: `Leverage above 1×, up to ${MAX_LIVE_LEVERAGE}×`,
        used: (s) => {
            const lev = s.risk?.leverage ?? 1;
            return lev !== 1 && lev <= MAX_LIVE_LEVERAGE;
        },
        levels: { research: "supported", deployable: "lossy" },
        because: {
            deployable: "Liquidation is modelled from closed-bar highs and lows at the exchange's lowest maintenance margin, not from its mark price, so a real account would be closed out at least as often. Backtest and bot use the same model.",
        },
    },
    {
        id: "leverage-above-live-cap",
        field: "risk.leverage",
        title: `Leverage above ${MAX_LIVE_LEVERAGE}×`,
        used: (s) => (s.risk?.leverage ?? 1) > MAX_LIVE_LEVERAGE,
        levels: { research: "supported", deployable: "rejected" },
        because: {
            deployable: `Paper bots run at most ${MAX_LIVE_LEVERAGE}×. Past that one bar's range decides most trades, and the record is mostly liquidations.`,
        },
    },
    {
        id: "funding",
        field: "risk.applyFunding",
        title: "Perpetual funding",
        used: (s) => s.risk?.applyFunding === true,
        levels: { research: "supported", deployable: "lossy" },
        because: {
            deployable: "Each settlement is charged on the position's entry notional, not the exchange's mark price at that moment. Backtest and bot use the same rule, and the bot only learns of a settlement after it happens, so a feed that cannot fetch one in time is recorded as a gap.",
        },
    },
    {
        id: "foreign-market",
        field: "indicators[].source",
        title: "Cross-market or cross-timeframe indicators",
        used: (s) => s.indicators?.some((d) => d.source !== undefined) ?? false,
        levels: { research: "supported", deployable: "rejected" },
        because: {
            deployable: "A foreign-market indicator needs a second synchronised feed. Getting it wrong yields a series that is null forever and a bot that never trades.",
        },
    },
    {
        id: "lookback",
        field: "entry",
        title: "A value from an earlier bar (bars ago)",
        used: looksBack,
        levels: both("supported"),
        because: {
            research: "Read from the same bars the rule is evaluated on. The bot keeps that many more bars in its window, so the look-back is the same live as in a backtest.",
        },
    },
    {
        id: "formula",
        field: "indicators[].formula",
        title: "Formula indicators",
        used: (s) => s.indicators?.some((d) => d?.kind === "formula") ?? false,
        levels: both("supported"),
        because: {
            research: "Computed bar by bar from indicators the strategy already computes, by the same function in a backtest and a bot. A division by zero or a missing input is no value, never zero.",
        },
    },
    {
        id: "slower-timeframe",
        field: "indicators[].timeframe",
        title: "An indicator on a slower chart of the traded market",
        used: (s) => s.indicators?.some((d) => d?.timeframe !== undefined) ?? false,
        levels: both("supported"),
        because: {
            research: "The slower bars are built from the run's own bars and read from the bar that completes them, so a bot needs no second feed and computes them as its backtest does. The run's timeframe must divide the indicator's.",
        },
    },
    {
        id: "no-stop-loss",
        field: "risk.stopLossPct",
        title: "Running without a stop loss",
        used: (s) => {
            const v = s.risk?.stopLossPct;
            return v === undefined || v === null || !Number.isFinite(v) || v <= 0;
        },
        levels: { research: "supported", deployable: "rejected" },
        because: {
            deployable: "A live bot running with a stop nobody chose is worse than a deploy that was refused.",
        },
    },
    {
        id: "oversized-pct-equity",
        field: "risk.sizeValue",
        title: "Position sizing above 100% of equity",
        used: (s) => s.risk?.sizeMode === "pct_equity" && (s.risk?.sizeValue ?? 0) > 100,
        levels: { research: "supported", deployable: "rejected" },
        because: {
            deployable: "Committing more than the account holds is leverage by another name; set the leverage instead.",
        },
    },
    {
        id: "both-exits",
        field: "risk.takeProfitPct",
        title: "A take-profit and a trailing stop together",
        used: (s) => (s.risk?.takeProfitPct ?? 0) > 0 && (s.risk?.trailingStopPct ?? 0) > 0,
        levels: both("lossy"),
        because: {
            research: "Whichever the bar's range reaches first wins, and the adverse side is resolved first. Without tick data that is a choice, not a fact.",
        },
    },
    {
        id: "unnamed-multi-output",
        field: "indicators[].output",
        title: "A multi-output indicator read without naming an output",
        used: (s) => {
            const kinds = new Map((s.indicators ?? []).map((d) => [d.id, d.kind]));
            let found = false;
            const walk = (node) => {
                if (!node || found)
                    return;
                if (isConditionGroup(node))
                    return node.children.forEach(walk);
                for (const side of [node.left, node.right]) {
                    if (side.kind !== "indicator" || side.output !== undefined)
                        continue;
                    const kind = kinds.get(side.ref);
                    if (kind && isMultiOutput(kind))
                        found = true;
                }
            };
            walk(s.entry);
            walk(s.exit);
            return found;
        },
        levels: both("lossy"),
        because: {
            research: "Defaulted to the indicator's primary output. The two prototypes disagreed on which that was, so name it.",
        },
    },
    {
        id: "cumulative-level",
        field: "entry",
        title: "A running total (OBV, A/D, PVT) compared with a fixed number",
        used: (s) => {
            const kinds = new Map((s.indicators ?? []).map((d) => [d.id, d.kind]));
            let found = false;
            const walk = (node) => {
                if (!node || found)
                    return;
                if (isConditionGroup(node))
                    return node.children.forEach(walk);
                const sides = [node.left, node.right];
                const running = sides.some((side) => {
                    if (side?.kind !== "indicator")
                        return false;
                    const kind = kinds.get(side.ref);
                    return kind !== undefined && catalogEntry(kind).cumulative === true;
                });
                if (running && sides.some((side) => side?.kind === "const"))
                    found = true;
            };
            walk(s.entry);
            walk(s.exit);
            return found;
        },
        levels: { research: "supported", deployable: "lossy" },
        because: {
            deployable: "Its level depends on how much history came before: a bot's rolling buffer and a backtest's full history put it at different heights. Compare it with its own MA (or another running total) instead.",
        },
    },
    {
        id: "presentation",
        field: "indicators[].paneKind",
        title: "Chart presentation fields",
        used: (s) => s.indicators?.some((d) => d.paneKind !== undefined ||
            d.label !== undefined ||
            d.color !== undefined ||
            d.lineStyle !== undefined ||
            d.lineWidth !== undefined) ?? false,
        levels: both("ignored"),
        because: { research: "Carried so a chart round-trips. No engine reads them." },
    },
];
const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;
/**
 * Check a spec against a profile.
 *
 * Returns data, never throws — a caller's job is to show findings. The
 * structural checks below are **profile-independent**: they are not
 * capabilities, they are malformed input, and they are rejected under both.
 */
export function checkSpec(spec, profile = "research") {
    if (!PROFILE_IDS.includes(profile)) {
        throw new Error(`Unknown profile "${profile}". Expected one of ${PROFILE_IDS.join(", ")}.`);
    }
    const findings = [];
    const reject = (id, field, message) => {
        findings.push({ id, field, level: "rejected", message });
    };
    if (typeof spec !== "object" || spec === null) {
        return {
            profile,
            findings: [{ id: "shape", field: "", level: "rejected", message: "Not an object." }],
            deployable: false,
            startupBars: 0,
        };
    }
    // --- Structural: malformed input, refused under every profile ---------------
    if (typeof spec.name !== "string" || spec.name.trim() === "") {
        reject("name", "name", "A strategy needs a name.");
    }
    if (!Array.isArray(spec.indicators) || spec.indicators.length === 0) {
        reject("indicators", "indicators", "A strategy needs at least one indicator.");
    }
    else {
        const seen = new Set();
        spec.indicators.forEach((def, i) => {
            const at = `indicators[${i}]`;
            if (typeof def?.id !== "string" || !IDENTIFIER.test(def.id)) {
                reject("indicator-id", `${at}.id`, `"${String(def?.id)}" is not a valid identifier. Rules bind to it.`);
            }
            else if (seen.has(def.id)) {
                reject("indicator-id", `${at}.id`, `Duplicate indicator id "${def.id}".`);
            }
            else {
                seen.add(def.id);
            }
            if (!INDICATOR_KINDS.includes(def?.kind)) {
                reject("indicator-kind", `${at}.kind`, `Unknown indicator "${String(def?.kind)}".`);
            }
            if (def?.kind === "formula" && Object.keys(def?.params ?? {}).length > 0) {
                reject("indicator-param", `${at}.params`, "A formula takes no parameters.");
            }
            for (const [key, value] of Object.entries(def?.params ?? {})) {
                if (!Number.isFinite(value) || value <= 0) {
                    reject("indicator-param", `${at}.params.${key}`, `Must be a positive number, got ${String(value)}.`);
                }
            }
        });
    }
    for (const problem of indicatorExtensionProblems(spec.indicators)) {
        reject(problem.id, problem.field, `${problem.message[0].toUpperCase()}${problem.message.slice(1)}.`);
    }
    const ids = new Set((spec.indicators ?? []).map((d) => d?.id).filter(Boolean));
    const kinds = new Map((spec.indicators ?? []).map((d) => [d?.id, d?.kind]));
    const checkOperand = (operand, at) => {
        if (typeof operand !== "object" || operand === null)
            return reject("operand", at, "Must be an object.");
        const offset = offsetProblem(operand);
        if (offset)
            reject("operand-offset", `${at}.offset`, `${offset[0].toUpperCase()}${offset.slice(1)}.`);
        if (operand.kind === "const") {
            if (!Number.isFinite(operand.value))
                reject("operand", `${at}.value`, "Must be a finite number.");
        }
        else if (operand.kind === "price") {
            if (!PRICE_FIELDS.includes(operand.field)) {
                reject("operand", `${at}.field`, `Must be one of ${PRICE_FIELDS.join(", ")}.`);
            }
        }
        else if (operand.kind === "indicator") {
            if (!ids.has(operand.ref)) {
                reject("operand-ref", `${at}.ref`, `"${operand.ref}" names no indicator on this strategy.`);
            }
            else if (operand.output !== undefined) {
                const kind = kinds.get(operand.ref);
                const outputs = kind ? INDICATOR_OUTPUTS[kind] : undefined;
                if (outputs && !outputs.includes(operand.output)) {
                    reject("operand-output", `${at}.output`, `${kind} has no output "${operand.output}". Try ${outputs.join(", ")}.`);
                }
            }
        }
        else {
            reject("operand", `${at}.kind`, `Unknown operand kind.`);
        }
    };
    const checkTree = (node, at, depth = 0) => {
        if (depth > 20)
            return reject("tree-depth", at, "Nested too deeply.");
        if (typeof node !== "object" || node === null)
            return reject("tree", at, "Must be an object.");
        if (isConditionGroup(node)) {
            if (node.children.length === 0) {
                // `[].every()` is true. An always-true entry opens a position on every
                // bar; an always-false one runs forever taking none. Neither is what an
                // empty group meant.
                return reject("empty-group", at, "An empty condition group has no meaning. Remove it or fill it.");
            }
            return node.children.forEach((child, i) => checkTree(child, `${at}.children[${i}]`, depth + 1));
        }
        if (!COMPARISON_OPS.includes(node.op)) {
            return reject("operator", `${at}.op`, `Unknown operator "${String(node.op)}".`);
        }
        checkOperand(node.left, `${at}.left`);
        checkOperand(node.right, `${at}.right`);
    };
    if (spec.entry === undefined)
        reject("entry", "entry", "An entry condition is required.");
    else
        checkTree(spec.entry, "entry");
    if (spec.exit === undefined)
        reject("exit", "exit", "An exit condition is required.");
    else
        checkTree(spec.exit, "exit");
    if (typeof spec.risk !== "object" || spec.risk === null) {
        reject("risk", "risk", "A risk block is required.");
    }
    else {
        if (!POSITION_SIZE_MODES.includes(spec.risk.sizeMode)) {
            reject("size-mode", "risk.sizeMode", `Must be one of ${POSITION_SIZE_MODES.join(", ")}.`);
        }
        if (!Number.isFinite(spec.risk.sizeValue) || spec.risk.sizeValue <= 0) {
            reject("size-value", "risk.sizeValue", "Must be a positive number.");
        }
    }
    if (spec.direction !== undefined && !TRADE_DIRECTIONS.includes(spec.direction)) {
        reject("direction", "direction", `Must be one of ${TRADE_DIRECTIONS.join(", ")}.`);
    }
    // --- Capabilities: what this profile does with what the spec asked for ------
    for (const cap of CAPABILITIES) {
        if (!cap.used(spec))
            continue;
        const level = cap.levels[profile];
        findings.push({
            id: cap.id,
            field: cap.field,
            level,
            message: cap.because[profile] ?? `${cap.title}: ${level}.`,
        });
    }
    const deployable = !findings.some((f) => f.level === "rejected");
    return {
        profile,
        findings,
        deployable,
        startupBars: deployable ? specStartupBars(spec) : 0,
    };
}
//# sourceMappingURL=profile.js.map